import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { sessionsApi } from '../api/sessions';
import type {
  SessionServerMessage,
  SessionSnapshot,
  WsEvent,
  ControlEventType,
  ScrollAnchor,
} from '../types/session';

const WS_BASE = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws';
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

interface Options {
  sessionId: string;
  teamId?: string;
  onEvent: (event: WsEvent) => void;
  onSessionState: (snapshot: SessionSnapshot) => void | Promise<void>;
  onOpen?: () => void;
  onClose?: () => void;
  onConnectionState?: (state: ConnectionState) => void;
  onTerminalError?: (code: string) => void;
}

export type ConnectionState = 'connecting' | 'synchronizing' | 'connected' | 'reconnecting' | 'disconnected';

export class AccessTokenError extends Error {
  readonly terminal: boolean;

  constructor(message: string, terminal: boolean) {
    super(message);
    this.name = 'AccessTokenError';
    this.terminal = terminal;
  }
}

function readTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')));
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

export async function getFreshAccessToken(): Promise<string> {
  try {
    const existingToken = window.localStorage.getItem('access_token');
    const expiresAt = readTokenExpiry(existingToken);
    if (existingToken && expiresAt && Date.now() < expiresAt - 30_000) return existingToken;

    const refreshToken = window.localStorage.getItem('refresh_token');
    if (!refreshToken) {
      if (existingToken && expiresAt && Date.now() < expiresAt) return existingToken;
      throw new AccessTokenError('No valid access or refresh token is available.', true);
    }

    try {
      const response = await axios.post<{ access: string; refresh?: string }>(
        `${API_BASE}/auth/token/refresh/`,
        { refresh: refreshToken },
        { timeout: 10_000 },
      );
      if (!response.data?.access) throw new AccessTokenError('Token refresh returned no access token.', true);
      window.localStorage.setItem('access_token', response.data.access);
      if (response.data.refresh) window.localStorage.setItem('refresh_token', response.data.refresh);
      return response.data.access;
    } catch (error) {
      if (error instanceof AccessTokenError) throw error;
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      throw new AccessTokenError(
        status ? `Token refresh rejected (${status}).` : 'Token refresh failed temporarily.',
        status === 400 || status === 401 || status === 403,
      );
    }
  } catch (error) {
    if (error instanceof AccessTokenError) throw error;
    throw new AccessTokenError('Unable to read authentication tokens.', true);
  }
}

const TERMINAL_ERRORS = new Set(['ACCESS_DENIED', 'SESSION_ENDED', 'SESSION_NOT_FOUND']);
const BUFFER_LIMIT = 200;
const SOCKET_TIMEOUT_MS = 10_000;

function retryDelay(attempt: number): number {
  const ceiling = Math.min(1_000 * 2 ** attempt, 30_000);
  return Math.round(ceiling * (0.5 + Math.random() * 0.5));
}

function eventRevision(message: WsEvent): number | undefined {
  return 'revision' in message ? message.revision : undefined;
}

export function useSessionWS({
  sessionId,
  teamId,
  onEvent,
  onSessionState,
  onOpen,
  onClose,
  onConnectionState,
  onTerminalError,
}: Options) {
  const wsRef = useRef<WebSocket | null>(null);
  const synchronizedRef = useRef(false);
  const [connectionState, setConnectionStateValue] = useState<ConnectionState>('connecting');
  const [isSynchronized, setIsSynchronized] = useState(false);
  const onEventRef = useRef(onEvent);
  const onSessionStateRef = useRef(onSessionState);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onConnectionStateRef = useRef(onConnectionState);
  const onTerminalErrorRef = useRef(onTerminalError);

  useEffect(() => {
    onEventRef.current = onEvent;
    onSessionStateRef.current = onSessionState;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    onConnectionStateRef.current = onConnectionState;
    onTerminalErrorRef.current = onTerminalError;
  });

  useEffect(() => {
    let active = true;
    let terminal = false;
    let attempt = 0;
    let lastRevision = -1;
    let snapshotApplication = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let openTimer: ReturnType<typeof setTimeout> | null = null;
    let snapshotTimer: ReturnType<typeof setTimeout> | null = null;
    let bufferedMessages: SessionServerMessage[] = [];
    let generation = 0;

    const updateConnectionState = (state: ConnectionState) => {
      setConnectionStateValue(state);
      onConnectionStateRef.current?.(state);
    };

    const setSynchronized = (value: boolean) => {
      synchronizedRef.current = value;
      setIsSynchronized(value);
    };

    const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
      if (timer) clearTimeout(timer);
    };

    const clearNetworkTimers = () => {
      clearTimer(openTimer);
      clearTimer(snapshotTimer);
      openTimer = null;
      snapshotTimer = null;
    };

    const closeCurrentSocket = () => {
      const current = wsRef.current;
      wsRef.current = null;
      if (!current) return;
      current.onopen = null;
      current.onclose = null;
      current.onmessage = null;
      current.onerror = null;
      current.close();
    };

    const terminalDisconnect = (code: string) => {
      if (!active) return;
      terminal = true;
      setSynchronized(false);
      clearNetworkTimers();
      clearTimer(retryTimer);
      retryTimer = null;
      closeCurrentSocket();
      updateConnectionState('disconnected');
      onTerminalErrorRef.current?.(code);
      onCloseRef.current?.();
    };

    const scheduleRetry = () => {
      if (!active || terminal || retryTimer) return;
      clearNetworkTimers();
      setSynchronized(false);
      closeCurrentSocket();
      updateConnectionState('reconnecting');
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      const delay = retryDelay(attempt);
      attempt += 1;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void connect();
      }, delay);
    };

    const diagnoseClose = async (code: number) => {
      if (code === 4001) {
        terminalDisconnect('UNAUTHENTICATED');
        return;
      }
      if ((code === 1006 || code === 4003) && teamId) {
        try {
          const response = await sessionsApi.get(teamId, sessionId);
          if (response.data.status === 'ENDED') {
            terminalDisconnect('SESSION_ENDED');
            return;
          }
          if (code === 4003) {
            terminalDisconnect('ACCESS_DENIED');
            return;
          }
        } catch (error) {
          const status = axios.isAxiosError(error) ? error.response?.status : undefined;
          if (status === 401 || status === 403 || status === 404) {
            terminalDisconnect(status === 404 ? 'SESSION_NOT_FOUND' : 'ACCESS_DENIED');
            return;
          }
        }
      } else if (code === 4003) {
        terminalDisconnect('ACCESS_DENIED');
        return;
      }
      scheduleRetry();
    };

    const requestSnapshot = (socket: WebSocket) => {
      if (!active || socket !== wsRef.current || socket.readyState !== WebSocket.OPEN) return;
      setSynchronized(false);
      updateConnectionState('synchronizing');
      clearTimer(snapshotTimer);
      snapshotTimer = setTimeout(() => scheduleRetry(), SOCKET_TIMEOUT_MS);
      socket.send(JSON.stringify({ type: 'state_request' }));
    };

    const applyRoomEvent = (message: SessionServerMessage): boolean => {
      if (message.type === 'state_revision') {
        if (message.revision <= lastRevision) return true;
        if (message.revision !== lastRevision + 1) return false;
        lastRevision = message.revision;
        return true;
      }
      if (message.type === 'session_state' || message.type === 'error') return true;
      const revision = eventRevision(message);
      if (revision !== undefined) {
        if (revision <= lastRevision) return true;
        if (revision !== lastRevision + 1) return false;
        lastRevision = revision;
      }
      onEventRef.current(message);
      if (message.type === 'session_end') {
        terminal = true;
        setSynchronized(false);
        clearNetworkTimers();
        updateConnectionState('disconnected');
      }
      return true;
    };

    const applySnapshot = async (snapshot: SessionSnapshot, socket: WebSocket) => {
      const snapshotGeneration = generation;
      const application = ++snapshotApplication;
      clearTimer(snapshotTimer);
      snapshotTimer = null;
      try {
        await onSessionStateRef.current(snapshot);
      } catch {
        scheduleRetry();
        return;
      }
      if (!active || socket !== wsRef.current || snapshotGeneration !== generation || application !== snapshotApplication) return;
      lastRevision = snapshot.revision;
      if (snapshot.session.status === 'ENDED') {
        terminal = true;
        setSynchronized(false);
        clearNetworkTimers();
        closeCurrentSocket();
        updateConnectionState('disconnected');
        return;
      }
      const messages = (bufferedMessages as WsEvent[]).sort(
        (left, right) => (eventRevision(left) ?? 0) - (eventRevision(right) ?? 0),
      );
      bufferedMessages = [];
      for (const message of messages) {
        if (!applyRoomEvent(message)) {
          bufferedMessages = [message];
          requestSnapshot(socket);
          return;
        }
        if (terminal) return;
      }
      setSynchronized(true);
      attempt = 0;
      updateConnectionState('connected');
      onOpenRef.current?.();
    };

    const handleMessage = (socket: WebSocket, raw: string) => {
      let message: SessionServerMessage;
      try {
        message = JSON.parse(raw) as SessionServerMessage;
      } catch {
        return;
      }

      if (message.type === 'error') {
        if (TERMINAL_ERRORS.has(message.code)) terminalDisconnect(message.code);
        else scheduleRetry();
        return;
      }
      if (message.type === 'session_state') {
        void applySnapshot(message, socket);
        return;
      }
      if (!synchronizedRef.current) {
        if (eventRevision(message) === undefined) return;
        if (bufferedMessages.length >= BUFFER_LIMIT) {
          scheduleRetry();
          return;
        }
        bufferedMessages.push(message);
        return;
      }
      if (!applyRoomEvent(message)) {
        bufferedMessages = [message];
        requestSnapshot(socket);
      }
    };

    async function connect() {
      if (!active || terminal || wsRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        updateConnectionState('reconnecting');
        return;
      }
      generation += 1;
      const thisGeneration = generation;
      updateConnectionState(attempt === 0 ? 'connecting' : 'reconnecting');
      let token: string;
      try {
        token = await getFreshAccessToken();
      } catch (error) {
        if (!active || thisGeneration !== generation) return;
        if (error instanceof AccessTokenError && error.terminal) terminalDisconnect('UNAUTHENTICATED');
        else scheduleRetry();
        return;
      }
      if (!active || terminal || thisGeneration !== generation) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        updateConnectionState('reconnecting');
        return;
      }
      let socket: WebSocket;
      try {
        socket = new WebSocket(`${WS_BASE}/sessions/${sessionId}/?token=${encodeURIComponent(token)}`);
      } catch {
        scheduleRetry();
        return;
      }
      wsRef.current = socket;
      openTimer = setTimeout(() => scheduleRetry(), SOCKET_TIMEOUT_MS);

      socket.onopen = () => {
        if (!active || socket !== wsRef.current) return;
        clearTimer(openTimer);
        openTimer = null;
        requestSnapshot(socket);
      };
      socket.onmessage = (event) => {
        if (active && socket === wsRef.current) handleMessage(socket, event.data);
      };
      socket.onclose = (event) => {
        if (!active || socket !== wsRef.current) return;
        wsRef.current = null;
        clearNetworkTimers();
        setSynchronized(false);
        onCloseRef.current?.();
        void diagnoseClose(event.code);
      };
    }

    const handleOnline = () => {
      if (!active || terminal || wsRef.current || retryTimer) return;
      void connect();
    };
    const handleOffline = () => {
      clearTimer(retryTimer);
      retryTimer = null;
      if (!terminal) {
        clearNetworkTimers();
        setSynchronized(false);
        closeCurrentSocket();
        updateConnectionState('reconnecting');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    void connect();

    return () => {
      active = false;
      clearNetworkTimers();
      clearTimer(retryTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      setSynchronized(false);
      closeCurrentSocket();
    };
  }, [sessionId, teamId]);

  const send = useCallback((payload: object) => {
    if (synchronizedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    } else {
      console.warn('[WS] Cannot send message before session synchronization completes');
    }
  }, []);

  const sendSongChange = useCallback((songId: string, setlistIndex?: number) => {
    const payload: Record<string, unknown> = { type: 'song_change', song_id: songId };
    if (setlistIndex !== undefined) payload.setlist_index = setlistIndex;
    send(payload);
  }, [send]);

  const sendControl = useCallback((control: ControlEventType, payload: Record<string, unknown> = {}) => {
    send({ type: 'control', control, payload });
  }, [send]);

  const sendScroll = useCallback((position: number, anchor?: ScrollAnchor | null) => {
    send({ type: 'scroll_sync', position, anchor: anchor ?? undefined });
  }, [send]);

  const sendLineHighlight = useCallback((highlightId: string, songId: string, sectionId: string, lineIndex: number) => {
    send({
      type: 'line_highlight',
      highlight_id: highlightId,
      song_id: songId,
      section_id: sectionId,
      line_index: lineIndex,
    });
  }, [send]);

  const sendFontSize = useCallback((size: string) => {
    send({ type: 'font_size_sync', size });
  }, [send]);

  const sendViewMode = useCallback((viewMode: string, capoFret?: number | null) => {
    const payload: Record<string, unknown> = { type: 'view_mode_change', view_mode: viewMode };
    if (viewMode === 'CAPO' && capoFret != null) payload.capo_fret = capoFret;
    send(payload);
  }, [send]);

  const sendEnd = useCallback(() => {
    send({ type: 'session_end' });
  }, [send]);

  const sendSetlistLoad = useCallback((setlistId: string) => {
    send({ type: 'setlist_load', setlist_id: setlistId });
  }, [send]);

  return {
    sendSongChange,
    sendControl,
    sendScroll,
    sendFontSize,
    sendLineHighlight,
    sendViewMode,
    sendEnd,
    sendSetlistLoad,
    connectionState,
    isSynchronized,
  };
}
